// --------------------------------------------------------------
// Created On: 2022-09-16
// Author: Zachary Thomas
//
// Last Modified: 2025-11-02
// Modified By: Zachary Thomas
//
// Copyright 2022 - 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useState, Fragment, useEffect } from "react";
import Modal from "../Modal/Modal";
import ModalHeader from "../ModalHeader/ModalHeader";
import ModalBody from "../ModalBody/ModalBody";
import ModalFooter from "../ModalFooter/ModalFooter";
import DeviceStatusValue from "./DeviceStatusValue/DeviceStatusValue";
import useApi from "../../hooks/useApi";
import Spinner from "../Spinner/Spinner";
import Error from "../Error/Error";
import { COPILOT_DEVICE_TYPE, API, ON_LATEST_FIRMWARE_DEVICE_STATUS } from "../../constants/miscellaneous";
import useReduxSelector from "../../hooks/useReduxSelector";
import styles from "./DeviceStatusModal.module.scss";

// Modal for status information for a single device.
export default function DeviceStatusModal(props: Props): Component {
  const [loading, setLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [firmwareUpdateInitialized, setFirmwareUpdateInitialized] = useState<boolean>(false);
  const [onLatestFirmware, setOnLatestFirmware] = useState<boolean | null>(null);
  const currentUser = useReduxSelector((state) => state.currentUser);

  // Calculates whether the current device is on the latest firmware version so that the "Update Firmware" button
  // can be disabled if it's on the latest version.
  useEffect(() => {
    const onLatestFirmwareStatus = props.statuses.find((status) => status.code === ON_LATEST_FIRMWARE_DEVICE_STATUS);
    if (onLatestFirmwareStatus === undefined) {
      setOnLatestFirmware(false);
    } else {
      setOnLatestFirmware(onLatestFirmwareStatus.value.toUpperCase() === "TRUE");
    }
  }, [props.deviceId, props.statuses]);

  // Get the most up to date status information if the device supports on demand status queries.
  useApi(
    () => {
      setErrorMessage("");
      if (props.deviceId > 0 && props.deviceType === COPILOT_DEVICE_TYPE) {
        setLoading(true);
        return true;
      } else {
        return false;
      }
    },
    {
      method: "POST",
      url: `${API}/company/${currentUser.companyId}/device/${props.deviceId}/status`,
    },
    async (response: Response, responseBody: ResponseBody) => {
      if (response.ok && responseBody && responseBody.monitoringDevices.length === 1) {
        props.onUpdateStatus(responseBody.monitoringDevices[0]);
        setErrorMessage("");
      } else {
        setErrorMessage("Unable to query live status information. Please try again later.");
      }
      setLoading(false);
    },
    [props.deviceId]
  );

  function handleCloseModal(): void {
    props.onClose();
    setFirmwareUpdateInitialized(false);
  }

  return (
    <Fragment>
      <Spinner loading={loading} />

      <Modal
        show={props.showModal}
        onHide={() => handleCloseModal()}
        backdropClassName={`${styles.modal} ${styles.backdrop}`}
        style={{ zIndex: "var(--modal-z-index)" }}
        size="lg"
        animation
        centered
      >
        <ModalHeader>
          <h5 className="font-weight-bold">Device Status</h5>
        </ModalHeader>

        <ModalBody>
          <div className={styles.deviceInfo}>
            {props.deviceType} {props.deviceIdentifier}
          </div>
          {props.statuses.length > 0 ? (
            <table className={`${styles.table} table mb-0 pb-0`}>
              <thead className={styles.thead}>
                <tr>
                  <th className={styles.th}>Name</th>
                  <th className={styles.th}>Value</th>
                  <th className={styles.th}>Info</th>
                </tr>
              </thead>
              <tbody>
                {props.statuses.map((deviceStatus) => (
                  <Fragment key={deviceStatus.name}>
                    {deviceStatus.code !== ON_LATEST_FIRMWARE_DEVICE_STATUS && (
                      <DeviceStatusValue
                        loading={loading}
                        firmwareUpdateInitialized={firmwareUpdateInitialized}
                        name={deviceStatus.name}
                        deviceId={props.deviceId}
                        description={deviceStatus.description}
                        value={deviceStatus.value}
                        onLatestFirmware={onLatestFirmware}
                        onUpdateFirmware={() => setFirmwareUpdateInitialized(true)}
                      />
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={styles.noStatus}>This device is not reporting any status information.</div>
          )}
          <div className="my-3 mb-0">
            <Error message={errorMessage} />
          </div>
        </ModalBody>

        <ModalFooter>
          <button className="btn btn-secondary" type="button" onClick={() => handleCloseModal()}>
            Close
          </button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
}

interface Props {
  showModal: boolean;
  deviceId: number;
  deviceIdentifier: string;
  deviceType: string;
  statuses: Status[];
  onClose: () => void;
  onUpdateStatus: (monitoringDevice: Device) => void;
}

interface Status {
  code: string;
  description: string;
  deviceStatusId: number;
  name: string;
  value: string;
}

interface Device {
  deviceId: number;
  deviceType: string;
  deviceIdentifier: string;
  statuses: Status[];
}

interface ResponseBody {
  monitoringDevices: Device[];
}
